import { ref } from 'vue';

import PasswordRecoveryReply from './PasswordRecoveryReply.js';

/**
 * The companies a portal user may choose between, and what happens when they pick one
 * (ClickUp 86e06umb3). Shared by the company-selection page shown after sign-in and the
 * company switcher in the header and on the profile page.
 *
 * The sign-in reply lists every company where the person has a portal role, in every state,
 * as `portalCompanys: [{id, name, state}]`. Only the active and inactive ones come with their
 * employee and company records, so only those can be entered. Locked and suspended companies
 * are shown, labelled "Contact support", and the server refuses them. Companies where the
 * person is only a driver are never listed.
 *
 * An older server sends no `portalCompanys`; the list then falls back to the companies of the
 * person's employees, as before, and no CompanyEnterCommandOperation is sent (that server has
 * no such command).
 *
 * The server also refuses any later portal request for a locked or suspended company
 * (AccountLockedId), an inactive one the person is only a mechanic at (CompanyNotActiveId), or
 * one they only drive at (NoPortalAccess); guardRefusal() picks those out for the app-wide
 * notice. A `portalCompanys` entry may carry `enterable: false`, which the list follows
 * whatever the company's state.
 */
export default class CompanyChoices {

  static Status = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    CONTACT_SUPPORT: 'contactSupport',
  };

  static Outcomes = {
    ENTERED: 'entered',
    REFUSED: 'refused',
    FAILED: 'failed',
  };

  static LOCKED_ID = 'AccountLockedId';

  // A mechanic at an inactive company: only owners, managers and ReadyChek administrators may
  // use an inactive one. Handled exactly like a locked company.
  static NOT_ACTIVE_ID = 'CompanyNotActiveId';

  // Refusals shown with the server's support hint, like a locked company.
  static SUPPORT_IDS = ['AccountLockedId', 'CompanyNotActiveId'];

  // Refused because the person only drives at that company, or has no employee there.
  static NO_ACCESS_ID = 'NoPortalAccess';

  static ENTER_EVENT = 'CompanyEnterCommandOperation';

  static TIMEOUT_MS = 10000;

  // Companies the server has refused since the last sign-in reply, by id. The reply's list is
  // fixed at sign-in, so a company locked mid-session would still read "Inactive" without this.
  // Tied to the sign-in reply it was learnt under: a new sign-in starts from the server's list.
  static _refused = { signinRid: null, ids: new Set() };

  // Bumped on every change to _refused, so the pages' computed lists recompute.
  static refusedVersion = ref(0);

  static LabelKeys = {
    active: 'domain_state_active',
    inactive: 'domain_state_inactive',
    contactSupport: 'company_select_state_contact_support',
  };

  /** The `portalCompanys` list from the latest sign-in reply, or null from an older server. */
  static portalCompanys(signinEvent) {
    const payload = signinEvent && signinEvent.Payload;
    const list = payload && payload.portalCompanys;
    return Array.isArray(list) ? list : null;
  }

  /** Active (or an older record with no state), inactive, or "contact support" for anything else. */
  static statusOf(state) {
    const value = typeof state === 'string' ? state.trim().toLowerCase() : '';
    if (value === '' || value === 'active' || value.startsWith('active:')) {
      return CompanyChoices.Status.ACTIVE;
    }
    if (value === 'inactive') {
      return CompanyChoices.Status.INACTIVE;
    }
    return CompanyChoices.Status.CONTACT_SUPPORT;
  }

  /**
   * @param portalCompanys the sign-in reply's list, or null from an older server
   * @param enterable [{id, name, state}] the companies the person has an employee record at
   * @returns rows sorted by name: {id, name, state, status, enterable}
   */
  static rows(portalCompanys, enterable, refusedIds = null) {
    const enterableIds = new Set((enterable || []).map((c) => c.id));
    const source = portalCompanys || enterable || [];
    const seen = new Set();
    const rows = [];
    for (const company of source) {
      if (!company || !company.id || seen.has(company.id)) continue;
      seen.add(company.id);
      // The server's own verdict when it gives one (an inactive company is closed to its
      // mechanics); the state decides only for a server that does not send it.
      const status = (refusedIds && refusedIds.has(company.id)) || company.enterable === false
        ? CompanyChoices.Status.CONTACT_SUPPORT
        : CompanyChoices.statusOf(company.state);
      rows.push({
        id: company.id,
        name: company.name || '',
        state: company.state || '',
        status,
        enterable: status !== CompanyChoices.Status.CONTACT_SUPPORT && enterableIds.has(company.id),
      });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }

  /** How many companies the person would choose between: decides whether the dialog shows. */
  static count(portalCompanys, enterable) {
    return CompanyChoices.rows(portalCompanys, enterable).length;
  }

  /** Whether this company may be entered without asking the person to choose again. */
  static isEnterable(portalCompanys, enterable, companyId, refusedIds = null) {
    const row = CompanyChoices.rows(portalCompanys, enterable, refusedIds).find((r) => r.id === companyId);
    return !!row && row.enterable;
  }

  static hintOf(error) {
    return error && typeof error.hint === 'string' ? error.hint.trim() : '';
  }

  /**
   * What the reply to a CompanyEnterCommandOperation means.
   *
   * @param reply the raw reply, or null when none came in time
   * @param companyId the company asked for; an OK reply names it as payload.Company
   * @returns {{outcome: string, message: string|null}}
   */
  static classifyEnter(reply, lockedFallback, failedMessage, companyId = null) {
    if (!reply) {
      return { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    const error = reply.error;
    if (error) {
      if (CompanyChoices.SUPPORT_IDS.includes(error.messageId)) {
        return { outcome: CompanyChoices.Outcomes.REFUSED, message: CompanyChoices.hintOf(error) || lockedFallback };
      }
      if (error.messageId === CompanyChoices.NO_ACCESS_ID) {
        return { outcome: CompanyChoices.Outcomes.FAILED, message: CompanyChoices.hintOf(error) || failedMessage };
      }
      return { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    const entered = reply.Payload && reply.Payload.Company;
    if (companyId && entered && entered !== companyId) {
      // An OK for some other company is not an OK for this one.
      return { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    return { outcome: CompanyChoices.Outcomes.ENTERED, message: null };
  }

  /**
   * A server refusal of an ordinary portal request because of the company it was for, or null.
   * Only while inside a company: the selection page and sign-in handle their own replies.
   *
   * @returns {{messageId: string, message: string}|null}
   */
  static guardRefusal(event, path, messages) {
    const error = event && event.error;
    if (!error || typeof path !== 'string' || !path.startsWith('/portal/v3/operator/')) {
      return null;
    }
    if (CompanyChoices.SUPPORT_IDS.includes(error.messageId)) {
      return { messageId: error.messageId, message: CompanyChoices.hintOf(error) || messages.lockedFallback };
    }
    if (error.messageId === CompanyChoices.NO_ACCESS_ID) {
      return { messageId: error.messageId, message: CompanyChoices.hintOf(error) || messages.noAccessFallback };
    }
    return null;
  }

  /**
   * Decides a pick.
   *
   * @param row the chosen row from rows()
   * @param portalCompanys null from an older server: decided here, with no request
   * @param send () => Promise<reply|null>, sends the CompanyEnterCommandOperation
   */
  static async pick(row, portalCompanys, send, messages) {
    const { lockedFallback, failedMessage } = messages;
    if (!row) {
      return { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    if (!portalCompanys) {
      if (row.status === CompanyChoices.Status.CONTACT_SUPPORT) {
        return { outcome: CompanyChoices.Outcomes.REFUSED, message: lockedFallback };
      }
      return row.enterable
        ? { outcome: CompanyChoices.Outcomes.ENTERED, message: null }
        : { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    // Locked and suspended are sent too: the server gives the refusal its wording and tells support.
    const result = CompanyChoices.classifyEnter(await send(), lockedFallback, failedMessage, row.id);
    if (result.outcome === CompanyChoices.Outcomes.ENTERED && !row.enterable) {
      // The server let it in, but this page holds no records for it to open with.
      return { outcome: CompanyChoices.Outcomes.FAILED, message: failedMessage };
    }
    return result;
  }

  /** The ids refused under this sign-in reply; an empty set once a different reply arrives. */
  static refusedFor(signinEvent) {
    const rid = (signinEvent && signinEvent['@rid']) || null;
    if (CompanyChoices._refused.signinRid !== rid) {
      CompanyChoices._refused = { signinRid: rid, ids: new Set() };
    }
    return CompanyChoices._refused.ids;
  }

  /**
   * Remembers that the server refused this company with AccountLockedId or NoPortalAccess, so
   * its row reads "Contact support" and cannot be entered for the rest of the session.
   * @returns true when this was such a refusal
   */
  static markRefused(signinEvent, companyId, messageId) {
    if (!companyId || !(CompanyChoices.SUPPORT_IDS.includes(messageId) || messageId === CompanyChoices.NO_ACCESS_ID)) {
      return false;
    }
    const ids = CompanyChoices.refusedFor(signinEvent);
    if (!ids.has(companyId)) {
      ids.add(companyId);
      CompanyChoices.refusedVersion.value++;
    }
    return true;
  }

  // ---- Adapters onto the portal's domain and store ----

  /** The companies the signed-in user has an employee record at, as plain {id, name, state}. */
  static enterableFromDomain(domain, givenUser = null) {
    if (!domain) return [];
    const user = givenUser || domain.session().user();
    if (!user || user.isNew()) return [];
    return domain.employees().findByUserId(user.id()).companys().find().list()
      .filter((company) => company.isNotNew())
      .map((company) => ({ id: company.id(), name: company.name(), state: company.state() }));
  }

  static rowsFromStore(store) {
    // Read so a computed list recomputes when a company is refused.
    void CompanyChoices.refusedVersion.value;
    const signinEvent = store.getters.signin_event;
    return CompanyChoices.rows(CompanyChoices.portalCompanys(signinEvent),
      CompanyChoices.enterableFromDomain(store.getters.domain), CompanyChoices.refusedFor(signinEvent));
  }

  /**
   * pick() against the latest sign-in reply, sending the request through the store. A refusal
   * is remembered, so the row's label follows the server rather than the sign-in reply.
   */
  static async choose(store, row, messages) {
    const signinEvent = store.getters.signin_event;
    const portal = CompanyChoices.portalCompanys(signinEvent);
    let reply = null;
    const send = async () => {
      reply = await CompanyChoices.sendEnter(store, row.id);
      return reply;
    };
    const result = await CompanyChoices.pick(row, portal, send, messages);
    if (reply && reply.error) {
      CompanyChoices.markRefused(signinEvent, row.id, reply.error.messageId);
    }
    return result;
  }

  /**
   * Sends CompanyEnterCommandOperation for this company and resolves with the raw reply, or
   * null after timeoutMs. The company goes in Params.Company; the request's own Company and
   * Employee are the chosen company and its employee too, not the session's current ones, so
   * the server's per-request guard judges the same company.
   */
  static sendEnter(store, companyId, timeoutMs = CompanyChoices.TIMEOUT_MS) {
    const domain = store.getters.domain;
    const request = domain.events().initRequest({
      Company: companyId,
      Params: { Company: companyId },
      eventName: CompanyChoices.ENTER_EVENT,
      handler: CompanyChoices.ENTER_EVENT,
    });
    const user = domain.session().user();
    const employee = domain.employees().findByUserId(user.id()).findByCompanyId(companyId);
    if (employee.isNew()) {
      delete request.data['Employee'];
    } else {
      request.data['Employee'] = employee.id();
    }
    const rid = request.data['@rid'];
    const reply = PasswordRecoveryReply.waitFor(
      rid,
      (eventRid, listener) => store.dispatch('addListenerAction', { eventName: eventRid, listener }),
      (eventRid) => store.dispatch('removeListenerAction', eventRid),
      timeoutMs,
    );
    request.send();
    return reply;
  }
}
